<div class="bk-page apps">
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <p class="bk-eyebrow">USSD</p>
      <h1 class="bk-page__title">Applications</h1>
      @if (!statsLoaded && isLoading) {
        <span class="bk-skel apps__sub-skel" aria-hidden="true"></span>
      } @else {
        <!-- Gaps, not separators, so a wrapped line never starts with one -->
        <ul class="apps__summary" aria-label="Summary">
          @for (part of summaryParts; track part.label) {
            <li [attr.data-tone]="part.tone || null">
              @if (part.tone) {
                <span class="apps__dot" aria-hidden="true"></span>
              }
              <strong>{{ part.value }}</strong> {{ part.label }}
            </li>
          }
        </ul>
      }
    </div>

    <div class="bk-page__actions">
      <button type="button" class="bk-btn bk-btn--ghost" (click)="openSimulator()">
        <span class="bk-sym" aria-hidden="true">dialpad</span>
        Open simulator
      </button>
      <button type="button" class="bk-btn bk-btn--primary" (click)="addApplication()">
        <span class="bk-sym" aria-hidden="true">add</span>
        New application
      </button>
    </div>
  </header>

  <div class="bk-toolbar apps__toolbar">
    <app-search-input
      class="apps__search"
      placeholder="Search by name, alias or code"
      [ngModel]="searchTerm"
      (ngModelChange)="updateSearch($event)"
    />
    <app-status-filter
      [options]="statusOptions"
      [selectedValue]="statusFilter"
      (selectionChange)="updateStatusFilter($event)"
    />
    <app-status-filter
      label="Environment"
      triggerIcon="layers"
      [options]="environmentOptions"
      [selectedValue]="environmentFilter"
      (selectionChange)="updateEnvironmentFilter($event)"
    />
    <app-sort-dropdown
      [groups]="sortGroups"
      [selectedValue]="sort"
      (selectionChange)="updateSort($event)"
    />
  </div>

  @if (errorMessage) {
    <div class="bk-panel apps__error" role="alert">
      <span class="apps__error-dot" aria-hidden="true"></span>
      <p class="apps__error-text">{{ errorMessage }}</p>
      <button type="button" class="bk-btn bk-btn--ghost" (click)="fetchApplications()">
        <span class="bk-sym" aria-hidden="true">refresh</span>
        Try again
      </button>
    </div>
  }

  <div class="bk-panel apps__panel">
    <data-table
      [seamless]="true"
      label="USSD applications"
      [loading]="isLoading"
      [maxHeight]="'640px'"
      [skeletonColumns]="6"
      [resizable]="false"
    >
      @if (rows.length > 0 || isLoading) {
        <table class="bk-log">
          <thead>
            <tr>
              <th scope="col" class="col-app">Application</th>
              <th scope="col" class="col-code">Service code</th>
              <th scope="col" class="col-env">Environment</th>
              <th scope="col" class="col-country">Country</th>
              <th scope="col" class="col-status">Status</th>
              <th scope="col" class="col-created">Created</th>
              <th scope="col" class="col-actions"><span class="bk-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.app.id) {
              <tr
                class="is-link"
                tabindex="0"
                [attr.aria-label]="'Open ' + row.name"
                (click)="navigateToDetail(row.app.id)"
                (keydown.enter)="openFromKeyboard($event, row.app.id)"
              >
                <td class="col-app">
                  <span class="bk-msg">
                    <span class="bk-msg__subject">{{ row.name }}</span>
                    @if (row.detail) {
                      <span class="bk-msg__preview">{{ row.detail }}</span>
                    }
                  </span>
                </td>
                <td class="col-code"><span class="apps__code">{{ row.code }}</span></td>
                <td class="quiet col-env">{{ row.environment }}</td>
                <td class="quiet col-country">{{ row.country }}</td>
                <td class="col-status">
                  <app-status [text]="row.status.text" [status]="row.status.status" />
                </td>
                <td class="quiet col-created">{{ row.created }}</td>
                <td class="col-actions">
                  <span class="bk-row-actions">
                    <button
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-label]="row.app.serviceCode ? 'Test ' + row.name + ' in simulator' : row.name + ' has no service code to test'"
                      [matTooltip]="row.app.serviceCode ? 'Test in simulator' : 'No service code to test'"
                      [attr.aria-disabled]="!row.app.serviceCode || null"
                      (click)="$event.stopPropagation(); row.app.serviceCode && testInSimulator(row.app)"
                    >
                      <span class="bk-sym" aria-hidden="true">dialpad</span>
                    </button>
                    <button
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-label]="'Edit ' + row.name"
                      matTooltip="Edit"
                      (click)="$event.stopPropagation(); navigateToEdit(row.app.id)"
                    >
                      <span class="bk-sym" aria-hidden="true">edit</span>
                    </button>
                    <button
                      type="button"
                      class="bk-icon-btn apps__danger"
                      [attr.aria-label]="'Delete ' + row.name"
                      matTooltip="Delete"
                      (click)="$event.stopPropagation(); confirmDelete(row.app)"
                    >
                      <span class="bk-sym" aria-hidden="true">delete</span>
                    </button>
                    <span class="bk-sym row-go" aria-hidden="true">chevron_right</span>
                  </span>
                </td>
              </tr>
            }
          </tbody>
        </table>
      } @else if (!errorMessage) {
        <app-empty-state
          icon="dialpad"
          [message]="emptyMessage"
          [hint]="emptyHint"
          [filteredHint]="emptyHint"
          [query]="searchTerm"
          [filtered]="!!statusFilter || !!environmentFilter"
          [actionLabel]="isFiltered ? '' : 'New application'"
          actionIcon="add"
          size="medium"
          (action)="addApplication()"
          (clear)="clearFilters()"
        />
      }
    </data-table>
  </div>

  @if (showPagination) {
    <div class="apps__pagination">
      <app-pagination
        [totalPages]="totalPages"
        [currentPage]="currentPage"
        [totalItems]="totalElements"
        [pageSize]="pageSize"
        [loading]="isLoading"
        (changePage)="onPageChange($event)"
        (pageSizeChange)="onPageSizeChange($event)"
      />
    </div>
  }
</div>

<app-prompt-card
  title="Delete application"
  buttonText="Delete application"
  closeText="Keep it"
  impact="Subscribers who dial this code get no menu. This cannot be undone."
  impactIcon="warning"
  [openModal]="showDeleteConfirm"
  [loading]="isDeleting"
  (onClose)="cancelDelete()"
  (onButtonClick)="deleteApplication()"
>
  <p class="apps__confirm">
    Delete <strong>{{ selectedApp?.name }}</strong>
    @if (selectedApp?.serviceCode) {
      ({{ selectedApp?.serviceCode }})
    }?
  </p>
</app-prompt-card>
